/**
 * The release log: every change to a statutory framework (EYFS, the Ofsted
 * toolkit, KCSIE…) from the day it's published to the day Otii's change goes
 * live, with the safeguarding sign-off before it does. The evidence behind
 * statutory currency.
 *
 * Days to notice and days to go live are counted from publication and held
 * against each framework's targets (Admin); one still open counts to today.
 * Going live needs a sign-off dated on or before the day it went live, or a
 * reason it went without, which is then flagged.
 */
import { addDays, daysBetween, type ISODate } from "./dates";

export interface ReleaseFramework {
  id: string;
  name: string;
  /** Days from publication to noticing it; null for no target. */
  detectDays: number | null;
  /** Days from publication to going live; null for no target. */
  deployDays: number | null;
  active: boolean;
  sortOrder: number;
}

/** Who signs a change off before it goes live (a safeguarding lead, say). They don't need to sign in. */
export interface SignOffReviewer {
  id: string;
  name: string;
  role: string;
  active: boolean;
  sortOrder: number;
}

export type ReleaseStatus = "scoped" | "in_build" | "in_review" | "deployed" | "withdrawn";
export const RELEASE_STATUS_LABEL: Record<ReleaseStatus, string> = { scoped: "Scoped", in_build: "In build", in_review: "In review", deployed: "Live", withdrawn: "Withdrawn" };
export const RELEASE_OPEN: ReleaseStatus[] = ["scoped", "in_build", "in_review"];

export interface Release {
  id: string;
  ref: number;
  frameworkId: string;
  what: string;
  ownerId: string | null;
  /** Logged at the time, or pieced together afterwards. */
  entryType: "recorded" | "reconstructed";
  status: ReleaseStatus;
  publishedOn: ISODate | null;
  detectedOn: ISODate | null;
  deployedOn: ISODate | null;
  releaseRef: string;
  signOff: { reviewerId: string; signedOn: ISODate; evidenceUrl: string | null } | null;
  /** Went live without a sign-off before it: why. */
  withoutSignOff: string | null;
  riskIds: string[];
  notes: string;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export const releaseRef = (n: number) => `SC-${String(n).padStart(3, "0")}`;

export interface ReleaseTiming {
  /** Days from publication to noticing it (to today while not yet noticed). */
  toDetect: number | null;
  toDeploy: number | null;
  /** Still counting: not noticed, or not live, yet. */
  detectOpen: boolean;
  deployOpen: boolean;
  overDetect: boolean;
  overDeploy: boolean;
  /** The day it should be live by, if there's a target. */
  deployBy: ISODate | null;
  /** Live, but not signed off on or before the day it went live. */
  unsigned: boolean;
}

/** How long a change took, or has taken so far, against its framework's targets. */
export function releaseTiming(r: Pick<Release, "publishedOn" | "detectedOn" | "deployedOn" | "status" | "signOff">, fw: Pick<ReleaseFramework, "detectDays" | "deployDays"> | undefined, today: ISODate): ReleaseTiming {
  const counting = r.status !== "withdrawn";
  const span = (end: ISODate | null) => (r.publishedOn ? (end ? daysBetween(r.publishedOn, end) : counting ? daysBetween(r.publishedOn, today) : null) : null);
  const toDetect = span(r.detectedOn);
  const toDeploy = span(r.deployedOn);
  const over = (days: number | null, target: number | null | undefined) => days !== null && target !== null && target !== undefined && days > target;
  const deployBy = r.publishedOn && fw?.deployDays != null ? addDays(r.publishedOn, fw.deployDays) : null;
  return {
    toDetect,
    toDeploy,
    detectOpen: !r.detectedOn,
    deployOpen: !r.deployedOn,
    overDetect: counting && over(toDetect, fw?.detectDays),
    overDeploy: counting && over(toDeploy, fw?.deployDays),
    deployBy,
    unsigned: r.status === "deployed" && !signedInTime(r),
  };
}

export const signedInTime = (r: Pick<Release, "signOff" | "deployedOn">) => !!r.signOff && !!r.deployedOn && r.signOff.signedOn <= r.deployedOn;

/** The log's headline: open changes, the longest and average days to go live, and how many went live without sign-off. */
export function releaseStats(rs: Release[], frameworks: ReleaseFramework[], today: ISODate) {
  const fw = new Map(frameworks.map((f) => [f.id, f]));
  const timed = rs.map((r) => ({ r, t: releaseTiming(r, fw.get(r.frameworkId), today) }));
  const live = timed.filter((x) => x.r.status === "deployed" && x.t.toDeploy !== null).map((x) => x.t.toDeploy!);
  return {
    logged: rs.length,
    open: rs.filter((r) => RELEASE_OPEN.includes(r.status)).length,
    overTarget: timed.filter((x) => x.r.status !== "withdrawn" && (x.t.overDetect || x.t.overDeploy)).length,
    longest: live.length ? Math.max(...live) : null,
    average: live.length ? Math.round(live.reduce((a, b) => a + b, 0) / live.length) : null,
    unsigned: timed.filter((x) => x.t.unsigned).length,
  };
}
